import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Menu as MenuIcon, Search } from "lucide-react";
import { Logo } from "@/components/ui/Logo";
import { StatusDot } from "@/components/ui/Badge";
import {
  MOCK_ORG,
  MOCK_USER,
  MOCK_AGENT_PULSE,
} from "@/components/mocks/fixtures";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface TopBarProps {
  onOpenCommandPalette?: () => void;
  onOpenMobileNav?: () => void;
}

export function TopBar({
  onOpenCommandPalette,
  onOpenMobileNav,
}: TopBarProps = {}) {
  const navigate = useNavigate();
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    try {
      await api.post("/platform/auth/logout");
    } catch {
      // Best-effort. Either way we drop the user back to /signin.
    } finally {
      navigate("/signin", { replace: true });
    }
  }

  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 border-b border-[var(--color-graphite-500)]/40"
      style={{
        height: "var(--topbar-height)",
        background: "rgba(7, 8, 10, 0.78)",
        backdropFilter: "blur(18px) saturate(180%)",
        WebkitBackdropFilter: "blur(18px) saturate(180%)",
      }}
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenMobileNav}
          className="md:hidden h-8 w-8 rounded-[var(--radius-button)] text-[var(--color-slate-200)] hover:bg-[var(--color-graphite-700)] hover:text-[var(--color-snow)] transition-colors inline-flex items-center justify-center"
          aria-label="Open navigation"
        >
          <MenuIcon size={16} />
        </button>
        <Logo withWordmark={false} />
        <button
          type="button"
          onClick={() => setShowOrgMenu((s) => !s)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-[var(--radius-button)] px-2.5 h-8",
            "text-[14px] font-medium text-[var(--color-snow)]",
            "hover:bg-[var(--color-graphite-700)] transition-colors duration-200",
          )}
        >
          <span>{MOCK_ORG.name}</span>
          <ChevronDown size={14} className="text-[var(--color-slate-300)]" />
        </button>
        {showOrgMenu && (
          <div className="absolute left-12 top-12 z-40 w-56 rounded-[var(--radius-modal)] p-1.5 surface-raised shadow-xl-eon">
            <p className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--color-slate-300)]">
              Switch organization
            </p>
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-[6px] px-2 py-1.5 text-[14px] text-[var(--color-snow)] bg-[var(--color-graphite-600)]"
            >
              <span>{MOCK_ORG.name}</span>
              <StatusDot tone="mint" />
            </button>
            <button
              type="button"
              className="flex w-full items-center rounded-[6px] px-2 py-1.5 text-[14px] text-[var(--color-slate-200)] hover:bg-[var(--color-graphite-700)]"
            >
              EON internal
            </button>
          </div>
        )}
      </div>

      <div className="hidden md:flex flex-1 max-w-md mx-4">
        <button
          type="button"
          onClick={onOpenCommandPalette}
          className="flex w-full items-center gap-2 h-8 px-2.5 rounded-[var(--radius-button)] shadow-hairline bg-[rgba(255,255,255,0.02)] text-[13px] text-[var(--color-slate-300)] hover:text-[var(--color-slate-200)] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
        >
          <Search size={14} />
          <span>Search anything…</span>
          <span className="ml-auto font-mono text-[10px] text-[var(--color-slate-300)]">
            ⌘K
          </span>
        </button>
      </div>

      <div className="flex items-center gap-3">
        <LiveIndicator />
        <button
          type="button"
          onClick={() => setShowUserMenu((s) => !s)}
          className="h-8 w-8 rounded-full bg-[var(--color-graphite-600)] text-[12px] font-semibold text-[var(--color-snow)] flex items-center justify-center hover:bg-[var(--color-graphite-700)] transition-colors"
          aria-label="Account menu"
        >
          {MOCK_USER.initials}
        </button>
        {showUserMenu && (
          <div className="absolute right-4 top-12 z-40 w-56 rounded-[var(--radius-modal)] p-1.5 surface-raised shadow-xl-eon">
            <div className="px-2 py-1.5">
              <p className="text-[13px] text-[var(--color-snow)] font-medium">
                {MOCK_USER.name}
              </p>
              <p className="text-[12px] text-[var(--color-slate-300)]">
                {MOCK_USER.email}
              </p>
            </div>
            <div className="h-px bg-[var(--color-graphite-500)]/40 my-1" />
            <button
              type="button"
              onClick={() => {
                setShowUserMenu(false);
                navigate("/settings/profile");
              }}
              className="flex w-full items-center rounded-[6px] px-2 py-1.5 text-[13px] text-[var(--color-slate-200)] hover:bg-[var(--color-graphite-700)] hover:text-[var(--color-snow)] transition-colors"
            >
              Profile
            </button>
            <button
              type="button"
              disabled={signingOut}
              onClick={signOut}
              className="flex w-full items-center rounded-[6px] px-2 py-1.5 text-[13px] text-[var(--color-slate-200)] hover:bg-[var(--color-graphite-700)] hover:text-[var(--color-snow)] transition-colors disabled:opacity-50"
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

function LiveIndicator() {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 h-8 px-2.5 rounded-[var(--radius-button)] hover:bg-[var(--color-graphite-700)] transition-colors group"
      aria-label="View live agent activity"
    >
      <StatusDot tone="ember" />
      <span className="font-mono text-[12px] text-[var(--color-slate-200)] group-hover:text-[var(--color-snow)] transition-colors">
        Live: {MOCK_AGENT_PULSE.inFlight} wakes
      </span>
    </button>
  );
}
